import React, { useState, useEffect, useRef, useMemo, useCallback, type ElementRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useCurrentAppUser } from "@/hooks/useCurrentAppUser";
import { useConversationMessages } from "@/hooks/useConversationMessages";
import { useAppUsers } from "@/hooks/useAppUsers";
import { useCanActAsManager } from "@/hooks/useCanActAsManager";
import { useContactSessions } from "@/hooks/useContactSessions";
import { useSupportSession, useSupportInbox, DEMAND_TYPE_CONFIG } from "@/hooks/useSupportInbox";
import { useDetectInteractionType, useCreateInteraction } from "@/hooks/useSupportInteractions";
import { useAssociatePreTicket } from "@/hooks/useAssociatePreTicket";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from "@/components/ui/resizable";
import { 
  Loader2, 
  Phone, 
  ArrowLeft,
  Check,
  CheckCheck,
  Clock,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  Headphones,
  Wifi,
  WifiOff,
  Paperclip,
  X,
  FileText,
  BarChart3
} from "lucide-react";
import { compressImage, isCompressibleImage, WPPCONNECT_MAX_SIZE_KB } from "@/utils/imageCompression";
import { validateFileSize } from "@/utils/fileValidation";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import { toast } from "sonner";
import { useNavigate, useSearchParams } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { getAvatarColor, getFullPhoneE164 } from "@/lib/utils";
import { useMobileUI } from "@/contexts/MobileUIContext";
import { SessionList, ClientSidebar, SessionDivider, TicketAssociationBar } from "@/components/support";
import { SessionActions, EscalationPanel } from "@/components/customer-service";
import { NewSessionModal } from "@/components/customer-service/NewSessionModal";
import { MessageBubble } from "@/features/chat/components/MessageBubble";
import { MessageInput } from "@/features/chat/components/MessageInput";
import { SendTemplateModal } from "@/components/whatsapp/SendTemplateModal";
import { DateSeparator } from "@/features/chat/components/DateSeparator";
import type { Message as ChatMessage } from "@/features/chat/hooks/useConversation";
import type { SessionWithSla, DemandType } from "@/types/support.types";
import type { ContactSession } from "@/hooks/useContactSessions";

export default function CustomerService() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const sessionIdFromUrl = searchParams.get('session');
  
  const { data: currentAppUser } = useCurrentAppUser();
  const { users: appUsers } = useAppUsers();
  const { canActAsManager } = useCanActAsManager();
  // Inbox channel switch removed: Atendimento is now unified on the Twilio support number.
  const { sessions } = useSupportInbox();
  const detectInteractionType = useDetectInteractionType();
  const createInteraction = useCreateInteraction();
  const { associate: associatePreTicket, isAssociating, resolvePreTicket, isResolving } = useAssociatePreTicket();
  
  // Hook to fetch session from URL param (for deep-linking from tasks)
  const { data: sessionFromUrl } = useSupportSession(sessionIdFromUrl);
  
  // Helper to get sender name from app_user_id
  const getSenderName = useMemo(() => {
    const userMap = new Map(appUsers.map(u => [u.id, u]));
    return (userId: string | null): string | null => {
      if (!userId) return null;
      const user = userMap.get(userId);
      if (!user) return null;
      return user.first_name || user.display_name?.split(' ')[0] || null;
    };
  }, [appUsers]);
  
  // Session selection state
  const [selectedSession, setSelectedSession] = useState<SessionWithSla | null>(null);
  const [templateModalOpen, setTemplateModalOpen] = useState(false);
  const [highlightedSessionId, setHighlightedSessionId] = useState<string | null>(null);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [newMessage, setNewMessage] = useState("");
  const [mobileView, setMobileView] = useState<'list' | 'chat'>('list');
  const [showSidebar, setShowSidebar] = useState(false);
  const [showNewTicketFromBar, setShowNewTicketFromBar] = useState(false);
  const [replyingTo, setReplyingTo] = useState<ChatMessage | null>(null);
  const sidebarPanelRef = useRef<ElementRef<typeof ResizablePanel>>(null);
  
  // File attachment states
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadedFileUrl, setUploadedFileUrl] = useState<string | null>(null);
  const [uploadedFilePath, setUploadedFilePath] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isCompressing, setIsCompressing] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Mobile UI context
  const { setHideNavigation } = useMobileUI();
  
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleTextareaChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setNewMessage(e.target.value);
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 120) + 'px';
    }
  }, []);

  // Build phone number from session contact
  const contactPhone = useMemo(() => {
    if (!selectedSession?.contact_phone) return null;
    return selectedSession.contact_phone;
  }, [selectedSession]);
  
  // Get existing demand types for the selected contact (for disabling in "Nova Sessão" menu)
  const existingDemandTypes = useMemo(() => {
    if (!selectedSession?.contact_id || !sessions) return [];
    return sessions
      .filter(s => s.contact_id === selectedSession.contact_id && s.status !== 'resolved')
      .map(s => s.demand_type)
      .filter((dt): dt is DemandType => dt !== null);
  }, [sessions, selectedSession?.contact_id]);

  // Fetch all sessions for the current contact (for dividers)
  const { data: contactSessions } = useContactSessions(selectedSession?.contact_id || null);

  // Use the conversation messages hook
  const {
    messages,
    isLoading: loadingMessages,
    isConnected,
    sendMessage,
    refetch: refetchMessages,
  } = useConversationMessages({
    contactId: selectedSession?.contact_id || null,
    leadId: null,
    sessionId: selectedSession?.id || null,
    phoneNumber: contactPhone,
    limit: 200,
  });

  // Build messages with session dividers interleaved
  const messagesWithDividers = useMemo(() => {
    if (!contactSessions || contactSessions.length === 0) {
      return messages.map(m => ({ type: 'message' as const, data: m }));
    }
    
    const items: Array<
      | { type: 'divider'; data: ContactSession }
      | { type: 'message'; data: typeof messages[0]; belongsToSessionId?: string }
    > = [];
    
    let sessionIndex = 0;
    let currentSessionId: string | undefined = undefined;
    
    for (const msg of messages) {
      // Insert dividers for sessions created BEFORE this message
      while (
        sessionIndex < contactSessions.length &&
        new Date(contactSessions[sessionIndex].created_at) <= new Date(msg.created_at)
      ) {
        currentSessionId = contactSessions[sessionIndex].id;
        items.push({
          type: 'divider',
          data: contactSessions[sessionIndex],
        });
        sessionIndex++;
      }
      
      items.push({ type: 'message', data: msg, belongsToSessionId: currentSessionId });
    }
    
    // Add remaining dividers (sessions created after the last message)
    while (sessionIndex < contactSessions.length) {
      items.push({
        type: 'divider',
        data: contactSessions[sessionIndex],
      });
      sessionIndex++;
    }
    
    return items;
  }, [messages, contactSessions]);

  // Pending scroll target — set when user clicks a chamado; cleared once we
  // successfully scroll to its divider in the conversation.
  const [pendingScrollSessionId, setPendingScrollSessionId] = useState<string | null>(null);

  // Scroll to a specific session divider and highlight it.
  // Marks the target as pending; an effect retries until the divider mounts
  // (the click may trigger a session switch + messages refetch).
  const scrollToSession = useCallback((sessionId: string) => {
    setHighlightedSessionId(sessionId);
    setPendingScrollSessionId(sessionId);
  }, []);

  // Try to scroll whenever the rendered messages/dividers change while a
  // pending scroll target exists. Also retries with a short timer in case the
  // divider mounts after the next render commit.
  useEffect(() => {
    if (!pendingScrollSessionId) return;
    let cancelled = false;
    const start = performance.now();
    const tryScroll = () => {
      if (cancelled) return;
      const element = document.getElementById(`session-${pendingScrollSessionId}`);
      const container = messagesContainerRef.current;
      if (element && container) {
        const cRect = container.getBoundingClientRect();
        const eRect = element.getBoundingClientRect();
        const offset = (eRect.top - cRect.top) + container.scrollTop
          - (container.clientHeight / 2) + (element.clientHeight / 2);
        container.scrollTo({ top: Math.max(0, offset), behavior: 'smooth' });
        setPendingScrollSessionId(null);
        return;
      }
      if (performance.now() - start < 4000) {
        setTimeout(tryScroll, 80);
      } else {
        setPendingScrollSessionId(null);
      }
    };
    tryScroll();
    return () => {
      cancelled = true;
    };
  }, [pendingScrollSessionId, messagesWithDividers]);

  // Sync highlight when selectedSession changes
  useEffect(() => {
    if (selectedSession?.id) {
      setHighlightedSessionId(selectedSession.id);
    }
  }, [selectedSession?.id]);

  // Auto-select session from URL param (deep-link from tasks)
  useEffect(() => {
    if (sessionFromUrl && selectedSession?.id !== sessionFromUrl.id) {
      setSelectedSession(sessionFromUrl);
      if (window.innerWidth < 768) {
        setMobileView('chat');
      }
    }
  }, [sessionFromUrl]);

  // Update navigation visibility based on mobile view
  useEffect(() => {
    if (window.innerWidth < 768) {
      setHideNavigation(mobileView === 'chat');
    }
    return () => setHideNavigation(false);
  }, [mobileView, setHideNavigation]);

  const scrollToBottom = () => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      scrollToBottom();
    }, 50);
    return () => clearTimeout(timeoutId);
  }, [messages]);

  // Mark conversation as read after sending
  const markCurrentConversationAsRead = async () => {
    if (!selectedSession?.contact_id) return;
    
    await supabase
      .from('messages')
      .update({ read_at: new Date().toISOString() })
      .eq('channel', 'whatsapp')
      .eq('direction', 'inbound')
      .is('read_at', null)
      .eq('contact_id', selectedSession.contact_id);
  };

  const handleSelectSession = (session: SessionWithSla) => {
    setSelectedSession(session);
    if (window.innerWidth < 768) {
      setMobileView('chat');
    }
  };

  // Handle selecting a session from the TicketAssociationBar by ID.
  // Also schedules a scroll to the corresponding divider in the conversation
  // so the user lands on the right excerpt instead of the previous position.
  const handleSelectSessionById = async (sessionId: string) => {
    const { data } = await supabase
      .from('v_sessions_with_sla')
      .select('*')
      .eq('id', sessionId)
      .single();
    if (data) {
      setSelectedSession(data as SessionWithSla);
    }
    scrollToSession(sessionId);
  };

  // Handler when a new sibling session is created
  const handleNewSessionCreated = async (sessionId: string) => {
    // Fetch the new session and select it
    const { data } = await supabase
      .from('v_sessions_with_sla')
      .select('*')
      .eq('id', sessionId)
      .single();
    
    if (data) {
      setSelectedSession(data as SessionWithSla);
    }
  };

  // Handler for associating a pre-ticket to an existing session
  const handleAssociatePreTicket = async (targetSessionId: string) => {
    if (!selectedSession?.id || !selectedSession?.contact_id) return;
    await associatePreTicket({
      preTicketId: selectedSession.id,
      targetSessionId,
      contactId: selectedSession.contact_id,
    });
    // After association, select the target session
    await handleSelectSessionById(targetSessionId);
  };

  const handleResolvePreTicket = async () => {
    if (!selectedSession?.id) return;
    await resolvePreTicket({ preTicketId: selectedSession.id });
    setSelectedSession(null);
  };

  const handleBackToList = () => {
    setMobileView('list');
    setSelectedSession(null);
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedSession) return;

    const messageText = newMessage;
    setNewMessage("");
    if (textareaRef.current) textareaRef.current.style.height = 'auto';
    
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 50);

    const result = await sendMessage(messageText);
    if (result.success) {
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
      markCurrentConversationAsRead();

      // Auto-detect interaction type in background
      if (selectedSession.id) {
        try {
          const interactionType = await detectInteractionType.mutateAsync({
            sessionId: selectedSession.id,
            messageText,
          });
          if (interactionType) {
            await createInteraction.mutateAsync({
              session_id: selectedSession.id,
              contact_id: selectedSession.contact_id || undefined,
              type: interactionType,
              notes: messageText.substring(0, 200),
              auto_detected: true,
              detection_reason: `Auto-detected as ${interactionType}`,
            });
          }
        } catch (err) {
          console.error('[AutoDetect] Failed to detect/create interaction:', err);
        }
      }
    } else {
      setNewMessage(messageText);
      toast.error(result.error || "Erro ao enviar mensagem");
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleSendAudio = async (audioBlob: Blob) => {
    if (!audioBlob || !contactPhone) return;

    setSendingMessage(true);
    
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 50);

    try {
      const fileName = `audio-${Date.now()}.mp3`;

      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('whatsapp-media')
        .upload(`outbound/${fileName}`, audioBlob, {
          cacheControl: '3600',
          upsert: false,
          contentType: 'audio/mpeg'
        });

      if (uploadError) throw uploadError;

      const { data: publicUrlData } = supabase.storage
        .from('whatsapp-media')
        .getPublicUrl(uploadData.path);

      const result = await sendMessage('🎤 Mensagem de voz', {
        mediaType: 'audio',
        mediaUrl: publicUrlData?.publicUrl,
        mimetype: 'audio/mpeg',
      });

      if (result.success) {
        setTimeout(() => {
          messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        }, 100);
        toast.success("Áudio enviado");
        await markCurrentConversationAsRead();
      } else {
        toast.error(result.error || 'Erro ao enviar áudio');
      }
    } catch (err: any) {
      console.error('Error sending audio:', err);
      toast.error('Erro ao enviar áudio');
    } finally {
      setSendingMessage(false);
    }
  };

  // File attachment handlers
  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const validation = validateFileSize(file);
    if (!validation.valid) {
      toast.error(validation.error);
      return;
    }

    setIsUploading(true);
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
      const filePath = `outbound/${fileName}`;

      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('whatsapp-media')
        .upload(filePath, file, {
          contentType: file.type,
          upsert: false
        });

      if (uploadError) throw uploadError;

      const { data: publicUrlData } = supabase.storage
        .from('whatsapp-media')
        .getPublicUrl(uploadData.path);

      setSelectedFile(file);
      setUploadedFileUrl(publicUrlData?.publicUrl || null);
      setUploadedFilePath(uploadData.path);
      toast.success('Arquivo anexado');
    } catch (err) {
      console.error('Error uploading file:', err);
      toast.error('Erro ao enviar arquivo');
    } finally {
      setIsUploading(false);
    }
  };

  const handleSendFile = async () => {
    if (!selectedFile || !uploadedFileUrl) return;
    
    setSendingMessage(true);
    
    let fileToSend = selectedFile;
    let urlToSend = uploadedFileUrl;
    let wasCompressed = false;
    
    try {
      const fileSizeKB = selectedFile.size / 1024;
      
      if (isCompressibleImage(selectedFile.type) && fileSizeKB > WPPCONNECT_MAX_SIZE_KB) {
        setIsCompressing(true);
        
        try {
          const compressedBlob = await compressImage(selectedFile, { maxSizeKB: WPPCONNECT_MAX_SIZE_KB });
          const compressedFile = new File([compressedBlob], selectedFile.name.replace(/\.[^.]+$/, '.jpg'), { type: 'image/jpeg' });
          
          const fileName = `${Date.now()}-compressed-${Math.random().toString(36).substring(7)}.jpg`;
          const filePath = `outbound/${fileName}`;
          
          const { data: uploadData, error: uploadError } = await supabase.storage
            .from('whatsapp-media')
            .upload(filePath, compressedFile, {
              contentType: 'image/jpeg',
              upsert: false
            });
          
          if (uploadError) throw uploadError;
          
          const { data: publicUrlData } = supabase.storage
            .from('whatsapp-media')
            .getPublicUrl(uploadData.path);
          
          fileToSend = compressedFile;
          urlToSend = publicUrlData?.publicUrl || uploadedFileUrl;
          wasCompressed = true;
        } catch (compErr) {
          console.error('[handleSendFile] Compression failed, trying with original:', compErr);
        } finally {
          setIsCompressing(false);
        }
      }
      
      const mediaType = fileToSend.type.startsWith('image/') ? 'image' 
                      : fileToSend.type.startsWith('video/') ? 'video'
                      : 'document';
      
      const caption = newMessage.trim() || undefined;
      
      const result = await sendMessage(caption || `📎 ${selectedFile.name}`, {
        mediaType,
        mediaUrl: urlToSend,
        filename: fileToSend.name,
        mimetype: fileToSend.type,
        caption,
        storagePath: uploadedFilePath || undefined,
        fileSize: fileToSend.size,
      });

      if (result.success) {
        removeAttachment();
        setNewMessage('');
        setTimeout(() => {
          messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        }, 100);
        toast.success(wasCompressed ? 'Imagem comprimida e enviada' : 'Arquivo enviado');
        await markCurrentConversationAsRead();
      } else {
        toast.error(result.error || 'Erro ao enviar arquivo');
      }
    } catch (err) {
      console.error('Error sending file:', err);
      toast.error('Erro ao enviar arquivo');
    } finally {
      setSendingMessage(false);
      setIsCompressing(false);
    }
  };

  const removeAttachment = () => {
    setSelectedFile(null);
    setUploadedFileUrl(null);
    setUploadedFilePath(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleContactClick = () => {
    if (selectedSession?.contact_id) {
      navigate(createPageUrl("ContactDetail") + `?id=${selectedSession.contact_id}`);
    }
  };

  const handleCall = () => {
    if (!contactPhone || !selectedSession) return;
    
    window.dispatchEvent(new CustomEvent('initiate-call', {
      detail: {
        contactId: selectedSession.contact_id,
        phoneNumber: contactPhone,
        contactName: [selectedSession.contact_first_name, selectedSession.contact_last_name].filter(Boolean).join(' ') || selectedSession.contact_name
      }
    }));
  };

  const contactName = [selectedSession?.contact_first_name, selectedSession?.contact_last_name].filter(Boolean).join(' ') || selectedSession?.contact_name || 'Sem nome';

  // Adapt support Message → chat Message (so MessageBubble can render it)
  const toChatMessage = useCallback((msg: typeof messages[0]): ChatMessage => {
    const senderName = msg.sent_by_app_user_id ? getSenderName(msg.sent_by_app_user_id) : null;
    return {
      id: msg.id,
      contact_id: msg.contact_id,
      lead_id: msg.lead_id,
      body: msg.body,
      direction: msg.direction,
      status: msg.status,
      error_code: null,
      channel: msg.channel,
      source: msg.source,
      sent_at: msg.sent_at,
      received_at: msg.received_at,
      created_at: msg.created_at,
      read_at: msg.read_at,
      sent_by_app_user_id: msg.sent_by_app_user_id,
      twilio_message_sid: null,
      num_media: null,
      media_type: msg.media_type,
      media_storage_path: msg.media_storage_path,
      media_filename: msg.media_filename,
      media_mimetype: msg.media_mimetype,
      media_size_bytes: msg.media_size_bytes,
      sent_by: senderName ? { display_name: senderName, email: null } : (msg.sent_by_app_user_id ? null : null),
    } as ChatMessage;
  }, [getSenderName]);

  // Build quoted body when replying
  const buildQuotedBody = (reply: ChatMessage, newText: string): string => {
    const raw = (reply.body || '').replace(/^>\s?.+?(?:\n\n|\n)/, '').trim();
    const quoted = raw
      ? raw.slice(0, 200)
      : reply.media_mimetype?.startsWith('image/') ? '📷 Imagem'
      : reply.media_mimetype?.startsWith('audio/') ? '🎤 Mensagem de voz'
      : reply.media_mimetype?.startsWith('video/') ? '🎬 Vídeo'
      : 'Anexo';
    return `> ${quoted}\n\n${newText}`;
  };

  // Unified send: routes to file send if file attached, otherwise text (with quote prefix)
  const handleUnifiedSend = useCallback(async () => {
    if (selectedFile) {
      await handleSendFile();
      setReplyingTo(null);
      return;
    }
    if (!newMessage.trim() || !selectedSession) return;
    if (replyingTo) {
      const messageText = buildQuotedBody(replyingTo, newMessage);
      setNewMessage("");
      setReplyingTo(null);
      const result = await sendMessage(messageText);
      if (result.success) {
        setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
        markCurrentConversationAsRead();
      } else {
        setNewMessage(messageText);
        toast.error(result.error || "Erro ao enviar mensagem");
      }
    } else {
      await handleSendMessage();
    }
  }, [newMessage, selectedFile, replyingTo, selectedSession]);

  const handleSendInternalNote = useCallback(async (text: string) => {
    const body = text.trim();
    if (!body) return;
    if (!selectedSession?.contact_id) {
      toast.error("Selecione uma conversa para adicionar a nota");
      return;
    }
    const { error } = await supabase.from('messages').insert({
      contact_id: selectedSession.contact_id,
      channel: 'whatsapp',
      direction: 'outbound',
      source: 'internal_note',
      body,
      sent_by_app_user_id: currentAppUser?.id ?? null,
      sent_at: new Date().toISOString(),
    });
    if (error) {
      console.error('[internal_note] insert error', error);
      toast.error("Erro ao adicionar nota interna");
      return;
    }
    await refetchMessages();
    setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    toast.success("Nota interna adicionada");
  }, [selectedSession?.contact_id, currentAppUser?.id, refetchMessages]);

  // Render messages list (shared between mobile and desktop)
  const renderMessagesList = (withDividers: boolean) => {
    const items = withDividers ? messagesWithDividers : messages.map(m => ({ type: 'message' as const, data: m }));
    const GROUP_GAP_MS = 60 * 1000;
    let prevMsgTime: string | null = null;
    let prevMsgDirection: string | null = null;

    return items.map((item, index) => {
      if (item.type === 'divider') {
        prevMsgTime = null;
        prevMsgDirection = null;
        return (
          <SessionDivider
            key={`divider-${item.data.id}`}
            sessionId={item.data.id}
            demandType={item.data.demand_type}
            createdAt={item.data.created_at}
            isCurrentSession={item.data.id === selectedSession?.id}
            isHighlighted={item.data.id === highlightedSessionId}
            status={item.data.status}
            resolutionNotes={item.data.resolution_notes}
          />
        );
      }

      const msg = item.data;
      const ts = msg.sent_at || msg.received_at || msg.created_at;

      // System notice (e.g. channel transition): render as centered chip, not bubble.
      if (msg.source === 'system_notice') {
        prevMsgTime = null;
        prevMsgDirection = null;
        return (
          <div key={msg.id} className="flex items-center justify-center my-3">
            <div className="bg-wa-in/95 dark:bg-wa-in/90 px-3 py-1.5 rounded-md shadow-sm max-w-[80%]">
              <span className="text-[12px] text-wa-text-muted text-center block">
                {msg.body}
              </span>
            </div>
          </div>
        );
      }

      const showDate = !prevMsgTime || new Date(prevMsgTime).toDateString() !== new Date(ts).toDateString();
      const grouped = !showDate
        && prevMsgDirection === msg.direction
        && prevMsgTime !== null
        && (new Date(ts).getTime() - new Date(prevMsgTime).getTime()) < GROUP_GAP_MS;

      const isHighlighted = withDividers && (item as any).belongsToSessionId === highlightedSessionId;

      prevMsgTime = ts;
      prevMsgDirection = msg.direction;

      const node = (
        <div
          className={cn(
            "transition-colors duration-300",
            isHighlighted && "bg-primary/5 rounded-lg px-2 -mx-2"
          )}
        >
          <MessageBubble
            message={toChatMessage(msg)}
            grouped={grouped}
            showTail={!grouped}
            onReply={setReplyingTo}
          />
        </div>
      );

      return (
        <React.Fragment key={msg.id}>
          {showDate && <DateSeparator timestamp={ts} />}
          {node}
        </React.Fragment>
      );
    });
  };

  // Mobile layout (< md breakpoint)
  const mobileLayout = (
    <div className={cn(
      "flex flex-1 min-h-0 gap-0 overflow-hidden bg-background md:hidden",
      mobileView === 'chat' && "fixed inset-0 z-50"
    )}>
      {/* Session List - Mobile */}
      <div className={cn(
        "w-full",
        mobileView === 'chat' && "hidden"
      )}>
        <SessionList
          onSelectSession={handleSelectSession}
          selectedSessionId={selectedSession?.id || null}
        />
      </div>

      {/* Chat Area - Mobile */}
      <div className={cn(
        "flex-1 min-h-0 flex flex-col relative bg-muted/30 overflow-hidden",
        mobileView === 'list' && "hidden"
      )}>
        {selectedSession ? (
          <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
            {/* Chat Header */}
            <div className={cn(
              "px-4 py-3 border-b bg-card flex items-center gap-3 flex-shrink-0",
              mobileView === 'chat' && "pt-safe"
            )}>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={handleBackToList}
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
              
              <div className="relative cursor-pointer" onClick={handleContactClick}>
                <Avatar className="h-10 w-10">
                  <AvatarFallback className={cn("bg-gradient-to-br text-white", getAvatarColor(contactName))}>
                    {contactName.charAt(0).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
              </div>
              
              <div className="flex-1 cursor-pointer" onClick={handleContactClick}>
                <div className="flex items-center gap-2">
                  <h2 className="font-semibold text-sm">{contactName}</h2>
                  {selectedSession?.demand_type && DEMAND_TYPE_CONFIG[selectedSession.demand_type as DemandType] && (
                    <Badge variant="secondary" className="text-xs h-5">
                      {DEMAND_TYPE_CONFIG[selectedSession.demand_type as DemandType].label}
                    </Badge>
                  )}
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="flex items-center gap-1">
                    {isConnected ? (
                      <Wifi className="h-3 w-3 text-emerald-500" />
                    ) : (
                      <WifiOff className="h-3 w-3 text-amber-500 animate-pulse" />
                    )}
                  </div>
                  <span className="text-xs text-muted-foreground">{contactPhone}</span>
                </div>
              </div>
              
              <div className="flex items-center gap-1">
                {selectedSession && (
                  <SessionActions 
                    session={selectedSession}
                    existingDemandTypes={existingDemandTypes}
                    onSessionUpdated={() => {
                      queryClient.invalidateQueries({ queryKey: ['support-sessions'], refetchType: 'all' });
                      queryClient.invalidateQueries({ queryKey: ['support-contact-sessions-full'] });
                    }}
                    onNewSessionCreated={handleNewSessionCreated}
                  />
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCall}
                  className="h-8"
                >
                  <Phone className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Escalation Panel */}
            <EscalationPanel sessionId={selectedSession?.id || null} />
            <TicketAssociationBar
              contactId={selectedSession?.contact_id || null}
              currentSessionId={selectedSession?.id || null}
              currentSessionStatus={selectedSession?.status || null}
              onSelectSession={handleSelectSessionById}
              onNewTicket={() => setShowNewTicketFromBar(true)}
              onAssociatePreTicket={handleAssociatePreTicket}
              onResolvePreTicket={handleResolvePreTicket}
              isAssociating={isAssociating}
              isResolving={isResolving}
            />

            {/* Messages */}
            <div ref={messagesContainerRef} className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden wa-wallpaper px-[5%] py-3 scrollbar-hide">
              {loadingMessages ? (
                <div className="flex items-center justify-center h-full">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : (
                <div className="max-w-3xl mx-auto">
                  {renderMessagesList(true)}
                  <div ref={messagesEndRef} />
                </div>
              )}
            </div>

            {/* Input Area */}
            <div className={cn("flex-shrink-0", mobileView === 'chat' && "pb-safe")}>
              <MessageInput
                value={newMessage}
                onChange={setNewMessage}
                onSend={handleUnifiedSend}
                onSendAudio={handleSendAudio}
                onFileSelect={async (file) => {
                  const fakeEvent = { target: { files: [file], value: '' } } as unknown as React.ChangeEvent<HTMLInputElement>;
                  await handleFileSelect(fakeEvent);
                }}
                onOpenTemplates={() => setTemplateModalOpen(true)}
                onSendNote={handleSendInternalNote}
                selectedFile={selectedFile}
                isUploading={isUploading || isCompressing}
                onClearFile={removeAttachment}
                isSending={sendingMessage}
                replyingTo={replyingTo}
                onCancelReply={() => setReplyingTo(null)}
              />
            </div>
          </div>
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center text-muted-foreground">
              <Headphones className="w-20 h-20 mx-auto mb-4 opacity-20" />
              <h3 className="text-lg font-semibold mb-1">Atendimento ao Cliente</h3>
              <p className="text-sm">Selecione uma sessão para começar</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  // Desktop layout (>= md breakpoint) with resizable panels
  const desktopLayout = (
    <ResizablePanelGroup
      direction="horizontal"
      className="hidden md:flex flex-1 min-h-0 overflow-hidden bg-background"
      autoSaveId="customer-service-layout"
    >
      {/* Session List Panel */}
      <ResizablePanel defaultSize={25} minSize={15} maxSize={40}>
        <SessionList
          onSelectSession={handleSelectSession}
          selectedSessionId={selectedSession?.id || null}
        />
      </ResizablePanel>

      <ResizableHandle withHandle />

      {/* Chat Area Panel */}
      <ResizablePanel defaultSize={50} minSize={30}>
        <div className="flex-1 min-h-0 h-full flex flex-col relative bg-muted/30 overflow-hidden">
          {selectedSession ? (
            <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
              {/* Chat Header */}
              <div className="px-4 py-3 border-b bg-card flex items-center gap-3 flex-shrink-0">
                <div className="relative cursor-pointer" onClick={handleContactClick}>
                  <Avatar className="h-10 w-10">
                    <AvatarFallback className={cn("bg-gradient-to-br text-white", getAvatarColor(contactName))}>
                      {contactName.charAt(0).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                </div>
                
                <div className="flex-1 cursor-pointer" onClick={handleContactClick}>
                  <div className="flex items-center gap-2">
                    <h2 className="font-semibold text-sm">{contactName}</h2>
                    {selectedSession?.demand_type && DEMAND_TYPE_CONFIG[selectedSession.demand_type as DemandType] && (
                      <Badge variant="secondary" className="text-xs h-5">
                        {DEMAND_TYPE_CONFIG[selectedSession.demand_type as DemandType].label}
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="flex items-center gap-1">
                      {isConnected ? (
                        <Wifi className="h-3 w-3 text-emerald-500" />
                      ) : (
                        <WifiOff className="h-3 w-3 text-amber-500 animate-pulse" />
                      )}
                    </div>
                    <span className="text-xs text-muted-foreground">{contactPhone}</span>
                  </div>
                </div>
                
                <div className="flex items-center gap-1">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleCall}
                    className="h-8"
                  >
                    <Phone className="h-4 w-4 mr-1" />
                    Ligar
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => {
                      const panel = sidebarPanelRef.current;
                      if (panel) {
                        if (panel.isCollapsed()) {
                          panel.expand();
                          setShowSidebar(true);
                        } else {
                          panel.collapse();
                          setShowSidebar(false);
                        }
                      } else {
                        setShowSidebar(!showSidebar);
                      }
                    }}
                    className="hidden lg:flex h-8 w-8"
                  >
                    {showSidebar ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
                  </Button>
                </div>
              </div>

              {/* Escalation Panel */}
              <EscalationPanel sessionId={selectedSession?.id || null} />
              <TicketAssociationBar
                contactId={selectedSession?.contact_id || null}
                currentSessionId={selectedSession?.id || null}
                currentSessionStatus={selectedSession?.status || null}
                onSelectSession={handleSelectSessionById}
                onNewTicket={() => setShowNewTicketFromBar(true)}
                onAssociatePreTicket={handleAssociatePreTicket}
                onResolvePreTicket={handleResolvePreTicket}
                isAssociating={isAssociating}
                isResolving={isResolving}
              />

              {/* Messages */}
              <div ref={messagesContainerRef} className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden wa-wallpaper px-[5%] py-3 scrollbar-hide">
                {loadingMessages ? (
                  <div className="flex items-center justify-center h-full">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  </div>
                ) : (
                  <div className="max-w-3xl mx-auto">
                    {renderMessagesList(true)}
                    <div ref={messagesEndRef} />
                  </div>
                )}
              </div>

              {/* Input Area */}
              <div className="flex-shrink-0">
                <MessageInput
                  value={newMessage}
                  onChange={setNewMessage}
                  onSend={handleUnifiedSend}
                  onSendAudio={handleSendAudio}
                  onFileSelect={async (file) => {
                    const fakeEvent = { target: { files: [file], value: '' } } as unknown as React.ChangeEvent<HTMLInputElement>;
                    await handleFileSelect(fakeEvent);
                  }}
                  onOpenTemplates={() => setTemplateModalOpen(true)}
                  onSendNote={handleSendInternalNote}
                  selectedFile={selectedFile}
                  isUploading={isUploading || isCompressing}
                  onClearFile={removeAttachment}
                  isSending={sendingMessage}
                  replyingTo={replyingTo}
                  onCancelReply={() => setReplyingTo(null)}
                />
              </div>
            </div>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center text-muted-foreground">
                <Headphones className="w-20 h-20 mx-auto mb-4 opacity-20" />
                <h3 className="text-lg font-semibold mb-1">Atendimento ao Cliente</h3>
                <p className="text-sm">Selecione uma sessão para começar</p>
              </div>
            </div>
          )}
        </div>
      </ResizablePanel>

      <ResizableHandle withHandle />

      {/* Client Sidebar Panel */}
      <ResizablePanel 
        ref={sidebarPanelRef}
        defaultSize={25} 
        minSize={15} 
        maxSize={35}
        collapsible
        collapsedSize={0}
        onCollapse={() => setShowSidebar(false)}
        onExpand={() => setShowSidebar(true)}
      >
        <ClientSidebar
          contactId={selectedSession?.contact_id || null}
          sessionId={selectedSession?.id || null}
          onScrollToSession={scrollToSession}
          session={selectedSession || null}
          existingDemandTypes={existingDemandTypes}
          onSessionUpdated={() => {
            queryClient.invalidateQueries({ queryKey: ['support-sessions'], refetchType: 'all' });
            queryClient.invalidateQueries({ queryKey: ['support-contact-sessions-full'] });
          }}
          onNewSessionCreated={handleNewSessionCreated}
          canActAsManager={canActAsManager}
        />
      </ResizablePanel>
    </ResizablePanelGroup>
  );

  return (
    <>
      {mobileLayout}
      {desktopLayout}
      <NewSessionModal
        open={showNewTicketFromBar}
        onOpenChange={setShowNewTicketFromBar}
        onSessionCreated={handleNewSessionCreated}
        prefilledContactId={selectedSession?.contact_id}
        preTicketId={selectedSession?.status === 'pre_ticket' ? selectedSession?.id : null}
      />
      <SendTemplateModal
        open={templateModalOpen}
        onOpenChange={setTemplateModalOpen}
        phoneNumber={selectedSession?.contact_phone ?? ""}
        contactId={selectedSession?.contact_id ?? undefined}
        leadId={undefined}
        channel="support"
        sessionId={selectedSession?.id}
        onTemplateSent={async () => {
          // Insert one-time channel-transition notice if this contact has never
          // received an outbound message from the new Atendimento number.
          try {
            const SUPPORT_NUMBER = '+16893082229';
            const contactId = selectedSession?.contact_id;
            if (contactId) {
              const { count } = await supabase
                .from('messages')
                .select('id', { count: 'exact', head: true })
                .eq('contact_id', contactId)
                .eq('direction', 'outbound')
                .eq('from_e164', SUPPORT_NUMBER)
                .eq('source', 'twilio_divus');
              const { count: noticeCount } = await supabase
                .from('messages')
                .select('id', { count: 'exact', head: true })
                .eq('contact_id', contactId)
                .eq('source', 'system_notice')
                .eq('from_e164', SUPPORT_NUMBER);
              // Insert notice only if there's exactly one outbound (the template just sent)
              // and no prior system_notice exists for this number.
              if ((count ?? 0) <= 1 && (noticeCount ?? 0) === 0) {
                // Anchor the notice's created_at 1s BEFORE the just-inserted template
                // so it renders above the template (the timeline orders by created_at).
                // Fallback: 5s before now if the template row isn't visible yet.
                const { data: lastTemplate } = await supabase
                  .from('messages')
                  .select('created_at')
                  .eq('contact_id', contactId)
                  .eq('source', 'twilio_divus')
                  .eq('direction', 'outbound')
                  .eq('from_e164', SUPPORT_NUMBER)
                  .order('created_at', { ascending: false })
                  .limit(1)
                  .maybeSingle();
                const anchor = lastTemplate?.created_at
                  ? new Date(new Date(lastTemplate.created_at).getTime() - 1000).toISOString()
                  : new Date(Date.now() - 5000).toISOString();
                await supabase.from('messages').insert({
                  contact_id: contactId,
                  owner_session_id: selectedSession?.id ?? null,
                  direction: 'outbound',
                  channel: 'whatsapp',
                  source: 'system_notice',
                  status: 'system',
                  body: 'A partir daqui você está falando com o cliente pelo novo número de Atendimento: +1 (689) 308-2229.',
                  from_e164: SUPPORT_NUMBER,
                  to_e164: selectedSession?.contact_phone ?? null,
                  sent_at: anchor,
                  created_at: anchor,
                });
              }
            }
          } catch (err) {
            console.warn('[customer-service] Failed to insert channel-transition notice for template:', err);
          }
          refetchMessages();
          setTemplateModalOpen(false);
        }}
      />
    </>
  );
}
